import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import type { DropdownLocation } from "@redotech/redo-web/locations/location-dropdown";

export const CUSTOM_ORIGIN_LOCATION_ID = "__custom_origin__";

function getCustomOriginLocation(
  groups: readonly FulfillmentGroupData[],
): DropdownLocation | null {
  if (groups.length !== 1) {
    return null;
  }
  const group = groups[0];
  if (!group || group.selectedLocationId) {
    return null;
  }
  const origin = group.originAddress;
  if (!origin || origin.locationId) {
    return null;
  }
  return {
    id: CUSTOM_ORIGIN_LOCATION_ID,
    name: origin.name?.trim() || "Custom location",
  };
}

export function getSelectedLocationId(
  groups: readonly FulfillmentGroupData[],
): string | null {
  const mixed = areLocationsMixed(groups);
  if (mixed) {
    return null;
  }
  const uniqueSelectedLocationIds = new Set(
    groups.map((group) => group.selectedLocationId),
  );
  const selected = [...uniqueSelectedLocationIds][0] ?? null;
  if (selected === null && getCustomOriginLocation(groups)) {
    return CUSTOM_ORIGIN_LOCATION_ID;
  }
  return selected;
}

export function areLocationsMixed(
  groups: readonly FulfillmentGroupData[],
): boolean {
  const uniqueSelectedLocationIds = new Set(
    groups.map((group) => group.selectedLocationId),
  );
  return uniqueSelectedLocationIds.size > 1;
}

export function getLocationsForMove(
  groups: readonly FulfillmentGroupData[],
): readonly DropdownLocation[] {
  const locations = groups.reduce((acc, group) => {
    const groupLocations = (group.locationsForMove ?? []).filter((location) => {
      const isSelected = location.id === group.selectedLocationId;
      const isMovable =
        location.movable && location.unavailableLineItemsCount === 0;
      const isSelectedByFO = location.selectedByFO; // can be true even if the group has a manual location selected
      return isSelected || isMovable || isSelectedByFO;
    });
    if (acc.length === 0) {
      return groupLocations;
    }
    const filtered = acc.filter((location) =>
      groupLocations.some((l) => l.id === location.id),
    );
    return filtered;
  }, [] as DropdownLocation[]);
  const customOrigin = getCustomOriginLocation(groups);
  return customOrigin ? [customOrigin, ...locations] : locations;
}
